import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { addClasses } from '../../utils/eventListeners';
import { getToken } from '../../utils/localStorage';

import { format, isValid, isFuture } from 'date-fns';
import { useGoogleAnalytics } from '../common/hooks/useGoogleAnalytics';
import { authAPI } from '../../services/redwolfSdkAPI';
import useRequestRedwolfService from '../common/hooks/useRequestRedwolfService';
import useTechnicalErrorMessage from '../common/hooks/useTechnicalErrorMessage';
import * as Sentry from '@sentry/react';
import Spinner from '../common/components/Spinner';
import { DesktopDatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { SxProps, TextField } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';

interface reportAbuseFormPayload {
	reportingReason: string;
	otherReason: string;
	userReported: string;
	userReportedContactInfo: string;
	issueDate: string;
	additionalDetails: string;
}

const ReportAbuse = () => {
	const [reportAbuseForm, setReportAbuseForm] = useState({
		reportingReason: { value: '', label: '' },
		otherReason: '',
		vizioAccountName: '',
		emailOrPhone: '',
		additionalDetails: ''
	});
	const [errorMessage, setErrorMessage] = useState({
		vizioAccountName: '',
		dateOfIssue: '',
		otherReason: '',
		timeOfIssue: ''
	});
	const [dateOfIssue, setDateOfIssue]: any = useState(null);
	const [timeOfIssue, setTimeOfIssue]: any = useState(null);
	const [viewContent, setViewcontent] = useState(
		getToken() ? 'report-abuse-form' : 'report-abuse-signin'
	);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const reportingReasonRef = useRef<HTMLSelectElement>(null);
	const vizioAccountNameRef = useRef<HTMLInputElement>(null);
	const emailOrPhoneRef = useRef<HTMLInputElement>(null);
	const dateOfIssueRef = useRef<HTMLInputElement>(null);
	const otherReasonRef = useRef<HTMLInputElement>(null);
	const timeOfIssueRef = useRef<HTMLInputElement>(null);
	const additionalDetailsRef = useRef<HTMLTextAreaElement>(null);
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const navigate = useNavigate();
	const token = getToken();

	const theme = createTheme({
		typography: {
			htmlFontSize: 10
		},
		palette: {
			primary: { main: '#9e9e9e' }
		}
	});

	const propperSx: SxProps = {
		'& .Mui-focusVisible:focus': {
			backgroundColor: '#d8d8d8'
		},
		'& .PrivatePickersYear-yearButton:focus': {
			backgroundColor: '#d8d8d8'
		},
		'& .Mui-focusVisible:focus.Mui-selected': {
			backgroundColor: '#9e9e9e'
		},
		'& .PrivatePickersYear-yearButton:focus.Mui-selected': {
			backgroundColor: '#9e9e9e'
		}
	};

	const timePropperSx: SxProps = {
		'& .MuiClock-clock:focus, .MuiClock-clock:focus-within': {
			border: '1px solid #000'
		}
	};

	useEffect(() => {
		addClasses();

		if (getToken()) {
			setViewcontent('report-abuse-form');
			logGoogleAnalyticsEvent('VIZIOGRAMREPORT_WEB_OPEN');
		}

		const headerContainerElement = document.querySelector('#main-container');
		if (headerContainerElement) {
			headerContainerElement.classList.add('container-viziogram');
		}

		return () => {
			if (headerContainerElement) {
				headerContainerElement.classList.remove('container-viziogram');
			}
		};
	}, []);

	const handleChange = (event: any) => {
		const { name, value, selectedOptions } = event.target;
		setErrorMessage({ ...errorMessage, [name]: '' });

		if (name === 'reportingReason') {
			setReportAbuseForm({
				...reportAbuseForm,
				reportingReason: { value: value, label: selectedOptions[0].label }
			});

			setTimeout(() => {
				addClasses();

				if (reportingReasonRef.current) {
					reportingReasonRef.current.blur();
				}
			}, 0);

			logGoogleAnalyticsEvent('VIZIOGRAMREPORT_WEB_MENU', { item_title: selectedOptions[0].label });
		} else {
			setReportAbuseForm({ ...reportAbuseForm, [name]: value });
		}
	};

	const additionalDetailsHandleChange = (event: any) => {
		if (event.target.value.length < 5001) {
			setReportAbuseForm({ ...reportAbuseForm, additionalDetails: event.target.value });
		}
	};

	const dateOfIssueHandleChange = (newValue: any) => {
		setErrorMessage({ ...errorMessage, dateOfIssue: '' });
		if (newValue) {
			setDateOfIssue(newValue.$d);
		}
	};

	const timeOfIssueHandleChange = (newValue: any, keyboardInputValue?: string) => {
		if (newValue) {
			setTimeOfIssue(newValue.$d);
		}

		if (keyboardInputValue && 'apAP'.includes(keyboardInputValue.slice(0, 1))) {
			setErrorMessage({ ...errorMessage, timeOfIssue: 'Invalid date format.' });
		} else {
			setErrorMessage({ ...errorMessage, timeOfIssue: '' });
		}
	};

	const navigateToSignInPage = () => {
		navigate(`/login?redirect_url=${encodeURIComponent('/en/account/viziogram/report')}`);
	};

	const handleReportAbuseSubmit = () => {
		const isReportAbuseFormValid = {
			otherReason: false,
			vizioAccountName: false,
			dateOfIssue: false,
			timeOfIssue: false
		};
		const requiredFieldMessage = 'This is a required field.';
		const invalidDateFormat = 'Invalid date format.';
		const invalidTimeFormat = 'Invalid time format.';

		if (!reportAbuseForm.vizioAccountName.trim()) {
			errorMessage.vizioAccountName = requiredFieldMessage;
			isReportAbuseFormValid.vizioAccountName = true;
		}
		if (!dateOfIssue) {
			errorMessage.dateOfIssue = requiredFieldMessage;
			isReportAbuseFormValid.dateOfIssue = true;
		}
		if (dateOfIssue && (!isValid(dateOfIssue) || isFuture(dateOfIssue))) {
			errorMessage.dateOfIssue = invalidDateFormat;
			isReportAbuseFormValid.dateOfIssue = true;
		}
		if (timeOfIssue && !isValid(timeOfIssue)) {
			errorMessage.timeOfIssue = invalidTimeFormat;
			isReportAbuseFormValid.timeOfIssue = true;
		}
		if (reportAbuseForm.reportingReason.value === 'other' && !reportAbuseForm.otherReason.trim()) {
			errorMessage.otherReason = requiredFieldMessage;
			isReportAbuseFormValid.otherReason = true;
		}
		setErrorMessage({ ...errorMessage });

		const setFocus: any = Object.entries(isReportAbuseFormValid).find(([key, isFieldValid]) => {
			return isFieldValid === true;
		});

		if (setFocus) {
			setFocusToInput(setFocus[0]);
		} else {
			callReportAbuseFormAPI();
		}
	};

	const setFocusToInput = (setFocus: any) => {
		if (setFocus === 'vizioAccountName' && vizioAccountNameRef.current) {
			vizioAccountNameRef.current.focus();
		} else if (setFocus === 'dateOfIssue' && dateOfIssueRef.current) {
			dateOfIssueRef.current.focus();
		} else if (setFocus === 'otherReason' && otherReasonRef.current) {
			otherReasonRef.current.focus();
		} else if (setFocus === 'timeOfIssue' && timeOfIssueRef.current) {
			timeOfIssueRef.current.focus();
		}
	};

	const callReportAbuseFormAPI = () => {
		setIsSpinnerLoading(true);

		let issueDate: any = '';

		if (dateOfIssue && timeOfIssue) {
			issueDate = new Date(
				dateOfIssue.getFullYear(),
				dateOfIssue.getMonth(),
				dateOfIssue.getDate(),
				timeOfIssue.getHours(),
				timeOfIssue.getMinutes(),
				timeOfIssue.getSeconds()
			).toISOString();
		} else {
			issueDate = format(dateOfIssue, 'yyyy-MM-dd');
		}

		const reportAbuseFormPayload: reportAbuseFormPayload = {
			reportingReason: reportAbuseForm.reportingReason.label,
			otherReason: reportAbuseForm.otherReason.trim(),
			userReported: reportAbuseForm.vizioAccountName.trim(),
			userReportedContactInfo: reportAbuseForm.emailOrPhone.trim(),
			issueDate: issueDate,
			additionalDetails: reportAbuseForm.additionalDetails
		};

		requestRedwolfService(authAPI.createAbuseReport(token, reportAbuseFormPayload))
			.then((response: any) => {
				setIsSpinnerLoading(false);

				if (response.constructor.$metadata$.simpleName === 'Success') {
					logGoogleAnalyticsEvent('VIZIOGRAMREPORT_WEB_SUCCESS');
					setViewcontent('report-abuse-success');
					window.scrollTo(0, 0);
				} else {
					logGoogleAnalyticsEvent('VIZIOGRAMREPORT_WEB_FAIL');
					Sentry.captureException(new Error(response));
					showTechnicalErrorMessage();
				}
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				logGoogleAnalyticsEvent('VIZIOGRAMREPORT_WEB_FAIL', { error: err });
				Sentry.captureException(err);
				showTechnicalErrorMessage();
			});
	};

	useEffect(() => {
		if (vizioAccountNameRef.current && vizioAccountNameRef.current.value) {
			vizioAccountNameRef.current.classList.add('active');
		}
		if (emailOrPhoneRef.current && emailOrPhoneRef.current.value) {
			emailOrPhoneRef.current.classList.add('active');
		}
		if (dateOfIssueRef.current && dateOfIssueRef.current.value) {
			dateOfIssueRef.current.classList.add('active');
		}
		if (timeOfIssueRef.current && timeOfIssueRef.current.value) {
			timeOfIssueRef.current.classList.add('active');
		}
		if (otherReasonRef.current && otherReasonRef.current.value) {
			otherReasonRef.current.classList.add('active');
		}
		if (additionalDetailsRef.current && additionalDetailsRef.current.value) {
			additionalDetailsRef.current.classList.add('active');
		}
	}, [reportAbuseForm.reportingReason]);

	return (
		<>
			<div className="viziogram">
				<div className="gram-container">
					{viewContent === 'report-abuse-signin' && (
						<div className="title-info login-info">
							<h1 className="h1">Report an issue on VIZIOgram.</h1>
							<p className="h2">To report an issue please login to your VIZIO Account.</p>
							<div className="btn-group">
								<button type="submit" className="btn primary-button" onClick={navigateToSignInPage}>
									Sign In
								</button>
							</div>
						</div>
					)}

					{viewContent === 'report-abuse-form' && (
						<>
							<div className="title-info">
								<h1 className="h1">Report an issue on VIZIOgram.</h1>
								<h2 className="h2">
									Use the form below to report a violation of the VIZIO Account Terms of Service
									related to VIZIOgram.{' '}
								</h2>
							</div>

							<div className="forms-inputs">
								<fieldset className="form-list">
									<legend className="title">Overview</legend>

									<div className="select input-label form-group">
										<select
											className="form-control"
											name="reportingReason"
											onChange={handleChange}
											ref={reportingReasonRef}
											required
											aria-label="Select Reporting Reason"
											value={reportAbuseForm.reportingReason.value}>
											<option disabled value="">
												Reporting Reason*
											</option>
											<option value="report photo vidio violates">
												I’d like to report a photo or video that violates the VIZIO Account Terms of
												Service
											</option>
											<option value="copyright violation">
												I’d like to report a copyright violation
											</option>
											<option value="child sexula exploitation">
												I’d like to report a child sexual exploitation issue
											</option>
											<option value="underage user">I’d like to report an underage user</option>
											<option value="threat of violence">
												I’d like to report a threat of violence or physical harm
											</option>
											<option value="harassing me">
												An account is harassing me or someone else
											</option>
											<option value="private information shared">
												Private information is being shared without permission
											</option>
											<option value="other">Other</option>
										</select>
										<label className="control-label required">Reporting Reason</label>
									</div>

									{reportAbuseForm.reportingReason.value &&
										reportAbuseForm.reportingReason.value === 'other' && (
											<div className="form-list">
												<div className={`field ${errorMessage.otherReason ? 'has-error' : ''}`}>
													<div className="input-label form-group">
														<input
															id="enterOtherReason"
															aria-required="true"
															aria-describedby="otherReasonError"
															type="text"
															className="form-control"
															name="otherReason"
															onChange={handleChange}
															ref={otherReasonRef}
															value={reportAbuseForm.otherReason}
														/>
														<label htmlFor="enterOtherReason" className="control-label required">
															Enter other reason
														</label>
														<code id="otherReasonError">{errorMessage.otherReason}</code>
													</div>
												</div>
											</div>
										)}
								</fieldset>

								{reportAbuseForm.reportingReason.value &&
									reportAbuseForm.reportingReason.value !== 'copyright violation' && (
										<div className="form-reporting">
											<fieldset className="form-list">
												<legend className="title">The user You&apos;re Reporting</legend>
												<div
													className={`field ${errorMessage.vizioAccountName ? 'has-error' : ''}`}>
													<div className="input-label form-group">
														<input
															id="vizioAccountName"
															aria-describedby="vizioAccountNameError"
															aria-required="true"
															type="text"
															className="form-control"
															name="vizioAccountName"
															onChange={handleChange}
															ref={vizioAccountNameRef}
															value={reportAbuseForm.vizioAccountName}
														/>
														<label htmlFor="vizioAccountName" className="control-label required">
															VIZIO Account Name
														</label>
														<code id="vizioAccountNameError">{errorMessage.vizioAccountName}</code>
													</div>
												</div>
												<div className="input-label form-group">
													<input
														id="emailOrPhonenumber"
														aria-label="Email or Phone Number"
														type="text"
														className="form-control"
														name="emailOrPhone"
														onChange={handleChange}
														ref={emailOrPhoneRef}
														value={reportAbuseForm.emailOrPhone}
													/>
													<label htmlFor="emailOrPhonenumber" className="control-label">
														Email or Phone Number
													</label>
												</div>
											</fieldset>
											<fieldset className="form-list">
												<legend className="title">Incident Details</legend>
												<div className={`field ${errorMessage.dateOfIssue ? 'has-error' : ''}`}>
													<div className="input-label form-group">
														<LocalizationProvider dateAdapter={AdapterDayjs}>
															<ThemeProvider theme={theme}>
																<DesktopDatePicker
																	label="Date of Issue"
																	inputRef={dateOfIssueRef}
																	value={dateOfIssue}
																	PopperProps={{
																		sx: propperSx
																	}}
																	onChange={dateOfIssueHandleChange}
																	renderInput={(params) => (
																		<TextField
																			{...params}
																			fullWidth
																			required
																			inputProps={{
																				...params.inputProps,
																				'aria-describedby': 'dateOfIssueError'
																			}}
																			error={Boolean(errorMessage.dateOfIssue ? true : false)}
																		/>
																	)}
																/>
															</ThemeProvider>
														</LocalizationProvider>
														<code id="dateOfIssueError">{errorMessage.dateOfIssue}</code>
													</div>
												</div>
												<div className={`field ${errorMessage.timeOfIssue ? 'has-error' : ''}`}>
													<div className="input-label form-group">
														<LocalizationProvider dateAdapter={AdapterDayjs}>
															<ThemeProvider theme={theme}>
																<TimePicker
																	label="Time of Issue"
																	inputRef={timeOfIssueRef}
																	value={timeOfIssue}
																	onChange={timeOfIssueHandleChange}
																	disableMaskedInput={false}
																	PopperProps={{
																		sx: timePropperSx
																	}}
																	renderInput={(params) => (
																		<TextField
																			{...params}
																			fullWidth
																			inputProps={{
																				...params.inputProps,
																				'aria-describedby': 'timeOfIssueError',
																				placeholder: 'hh:mm AM or PM'
																			}}
																			error={Boolean(errorMessage.timeOfIssue ? true : false)}
																		/>
																	)}
																/>
															</ThemeProvider>
														</LocalizationProvider>
														<code id="timeOfIssueError">{errorMessage.timeOfIssue}</code>
													</div>
												</div>
												<div className="field">
													<div className="input-label textArea form-group">
														<textarea
															id="additionalDetails"
															className="form-control"
															rows={3}
															name="additionalDetails"
															aria-placeholder="enter maximum 5000 character"
															onChange={additionalDetailsHandleChange}
															ref={additionalDetailsRef}
															value={reportAbuseForm.additionalDetails}
														/>
														<label htmlFor="additionalDetails" className="control-label">
															Additional Details{' '}
														</label>
													</div>
													<p className="text-count">{`(${reportAbuseForm.additionalDetails.length}/5000)`}</p>
												</div>
											</fieldset>
											<div className="btn-group">
												<button
													type="submit"
													className="btn primary-button"
													onClick={handleReportAbuseSubmit}>
													Submit Feedback{' '}
												</button>
												<p className="req-all-txt">*Indicates required field. </p>
											</div>
										</div>
									)}

								{reportAbuseForm.reportingReason.value &&
									reportAbuseForm.reportingReason.value === 'copyright violation' && (
										<div className="tos-cnt">
											<p className="mr-bt2">
												If you believe that your work, content or information has been copied in a
												way that constitutes copyright infringement through the VIZIOgram feature,
												please submit your complaint by email or physical mail as follows:
											</p>
											<div className="tos-email mr-bt2">
												<p>
													Email: <br /> copyright@vizio.com
												</p>
											</div>
											<div className="tos-address mr-bt2">
												<p>
													Mailing Address: <br />
													VIZIO, Inc.
													<br />
													Attn: Copyright Agent
													<br />
													39 Tesla
													<br />
													Irvine,CA 92618
												</p>
											</div>

											<div className="inform-list">
												<p>
													You must provide the following information, which VIZIO may then forward
													to the alleged infringer:
												</p>
												<ul>
													<li>
														Identify and describe the User Content or other material on the Service
														that you believe infringes your content;
													</li>
													<li>
														Identify and describe where the content that you claim is infringing
														appeared or is located on the Service;
													</li>
													<li>Provide your address, telephone number and email address;</li>
													<li>
														Provide a statement, made under penalty of perjury, that: (a) you have a
														good faith belief that the disputed use is not authorized by the
														copyright owner, its agent or the law; (b) the information you have
														submitted is complete, accurate and true; and (c) you are the owner of
														the content involved or you are authorized to act on behalf of that
														owner; and
													</li>
													<li>Provide your physical or electronic signature.</li>
												</ul>
											</div>
										</div>
									)}
							</div>
						</>
					)}

					{viewContent === 'report-abuse-success' && (
						<div className="title-info thanks-info">
							<h1 className="h1">Thank you for sending this report.</h1>
							<p role="alert" aria-live="polite">
								We may contact you at the email address associated with your VIZIO Account if
								additional information is needed. Even if you do not hear from us, please be assured
								VIZIO takes this report seriously. The issue will be reviewed and any violations of
								VIZIO Account’s Terms of Service will be appropriately addressed.
							</p>
						</div>
					)}
				</div>
			</div>

			{isSpinnerLoading && <Spinner />}
		</>
	);
};

export default ReportAbuse;
